import Constants from '@/Constants';
import { includes } from '@/lib/array';
import { LessonEnrollment } from '@/models/LessonEnrollment';
import { ProgramClass } from '@/models/ProgramClass';
import { Status } from '@/models/Status';
import { analyticsService, LMSEvents } from '@/services/AnalyticsService';
import { lmsCourseService } from '@/services/LMSCourseService';
import { isWorkshopConfigured } from '@/services/WorkshopService';
import { ApplicationState } from '@/store';
import { CourseStudentView } from '@/store/list/types';
import React, { useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Button from 'react-bootstrap/Button';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import LMSContentCard from '../LMSContentCard';
import LMSCourseCard from '../LMSCourseCard';
import './style.scss';

const ProgramCourseLesson: React.FC = () => {
    const { courseStudentViewList, strings, programClass } = useSelector((state: ApplicationState) => ({
        programClass: state.program.currentProgramClassStudentView.classEnrollment?.programClass,
        courseStudentViewList: state.program.currentProgramClassStudentView.courseStudentViews,
        strings: state.locale.data,
    }));

    const [canShowContentLink, setCanShowContentLink] = useState(false);
    const [activePositions, setActivePositions] = useState<number[]>([0]);
    const [defaultActiveKey, setDefaultActiveKey] = useState<string>();

    const history = useHistory();

    const getConditionalText = (condition: boolean, trueResult: string, falseResult: string = ''): string => (condition ? trueResult : falseResult);

    const toggleLessons = (courseIndex: number) => {
        const arrayIndex = activePositions.findIndex((position: number) => position === courseIndex);

        if (arrayIndex > -1) {
            activePositions.splice(arrayIndex, 1);
            setActivePositions([...activePositions]);
        } else {
            setActivePositions([...activePositions, courseIndex]);
        }
    };

    const courseIsActive = (activePositions: number[], courseIndex: number) =>
        activePositions.findIndex((position: number) => position === courseIndex) > -1;

    const linkRouteLesson = (courseStudentView: CourseStudentView, lessonEnrollment: LessonEnrollment): string => {
        const { course, classEnrollment } = courseStudentView;

        if (lessonEnrollment.lesson.type === 'EvaluationLesson') {
            return `/lms/enrollments/${classEnrollment.id}/courses/${course.id}/${Constants.EVALUATIONS_RESOURCE_NAME}/${lessonEnrollment.lesson.id}`;
        } else {
            return `/lms/enrollments/${classEnrollment.id}/courses/${course.id}/${Constants.LESSONS_RESOURCE_NAME}/${lessonEnrollment.lesson.id}`;
        }
    };

    const scrollTo = (courseIndex: number) => {
        const courseDiv = document.getElementById(`course${courseIndex}`);

        if (courseDiv) {
            courseDiv.scrollIntoView();

            const scrolledY = window.scrollY;
            if (scrolledY) {
                window.scroll(0, scrolledY - 105);
            }
        }
    };

    const getCourseStudentViewIndex = (status: Status): number =>
        courseStudentViewList.findIndex((courseStudentView: CourseStudentView) => courseStudentView.courseEnrollment.status === status);

    useEffect(() => {
        if (courseStudentViewList.length > 0) {
            let index = getCourseStudentViewIndex(Constants.courseEnrollmentStatus.STARTED);
            if (index === -1) {
                index = getCourseStudentViewIndex(Constants.courseEnrollmentStatus.NOT_STARTED);
            }

            setActivePositions(index === -1 ? [0] : [index]);
            setDefaultActiveKey(index === -1 ? '0' : index.toString());

            setTimeout(() => scrollTo(index), 100);
        }
    }, [courseStudentViewList.length]);

    const logEvent = (entityId: number | undefined, entityName: string) => {
        analyticsService.logEvent(LMSEvents.ACCESS_COURSE_INSIDE_PROGRAM, {
            entityId,
            entityName,
            moduleId: Constants.LMS_MODULE_ID,
            entityType: Constants.entityTypes.COURSE,
        });
    };

    const { COMPLETED, FAILED, PASSED, INCOMPLETE } = Constants.courseEnrollmentStatus;
    const DONE = [COMPLETED, FAILED, PASSED];

    const isCourseDisabled = (courseStudentView: CourseStudentView, programClass: ProgramClass) => {
        const { course, courseEnrollment } = courseStudentView;

        if (course.type === 'REGULAR') return courseEnrollment.status === Constants.courseEnrollmentStatus.BLOCKED;

        if (course.type === 'WORKSHOP') return !isWorkshopConfigured(programClass.workshops, course.id);

        return false;
    };

    return (
        <div className="program-course-lesson-container">
            {courseStudentViewList &&
                courseStudentViewList.map((courseStudentView: CourseStudentView, courseIndex: number, array: CourseStudentView[]) => {
                    const { course, courseEnrollment } = courseStudentView;
                    const previousCourseStudentView: CourseStudentView = array[courseIndex - 1];
                    const statusDone = includes(DONE, courseEnrollment.status || INCOMPLETE);
                    const statusBlocked = isCourseDisabled(courseStudentView, programClass);
                    const previousStatusDone = includes(
                        DONE,
                        (previousCourseStudentView && previousCourseStudentView.courseEnrollment.status) || INCOMPLETE,
                    );
                    const courseActive = courseIsActive(activePositions, courseIndex);
                    if (courseEnrollment.status === 'NOT_STARTED') {
                        courseEnrollment.progressPercentage = 0;
                    }
                    return (
                        <React.Fragment key={courseIndex}>
                            <div className="course-container" id={`course${courseIndex}`}>
                                <div className="course-container__position">
                                    <div className="course-container__position__wrap">
                                        {courseIndex === 0 && <div className="d-flex flex-grow-1" />}
                                        {courseIndex > 0 && (
                                            <div className={`vertical-line ${getConditionalText(previousStatusDone, 'completed')}`} />
                                        )}

                                        <div className={`index-view ${getConditionalText(statusBlocked, 'blocked')}`}>
                                            <span>{courseIndex + 1}</span>
                                        </div>
                                        {courseIndex === courseStudentViewList.length - 1 && <div className="d-flex flex-grow-1" />}
                                        {courseIndex < courseStudentViewList.length - 1 && (
                                            <div className={`vertical-line ${getConditionalText(statusDone, 'completed')}`} />
                                        )}
                                    </div>
                                </div>
                                <div className="course-container__card" key={course.id}>
                                    <Link
                                        className={`link-to-course ${statusBlocked ? 'disable-link' : ''}`}
                                        onClick={() => logEvent(course.id, course.name)}
                                        to={{
                                            pathname: `/lms/enrollments/${courseStudentView.classEnrollment.id}/courses/${course.id}`,
                                            state: { goBackPath: history.location.pathname },
                                        }}
                                    >
                                        <LMSCourseCard
                                            course={course}
                                            courseEnrollment={courseEnrollment}
                                            disabled={statusBlocked}
                                            isCourseProgram={courseStudentView.classEnrollment.programClass.program.isCourseProgram}
                                            mode={'view'}
                                            programClass={programClass}
                                        />
                                    </Link>
                                </div>
                                <div className="d-flex flex-grow-1" />
                            </div>

                            {!statusBlocked && !!defaultActiveKey && course.lessons && course.lessons?.length > 0 && (
                                <Accordion defaultActiveKey={defaultActiveKey}>
                                    <Accordion.Toggle
                                        as={Button}
                                        className="btn btn-toggle"
                                        eventKey={courseIndex.toString()}
                                        onClick={() => {
                                            toggleLessons(courseIndex);
                                        }}
                                    >
                                        <span>{courseActive ? strings.HIDE_LESSONS : strings.SHOW_LESSONS}</span>
                                        <i className="material-icons arrow-icon">
                                            {courseActive ? Constants.icons.KEYBOARD_ARROW_UP : Constants.icons.KEYBOARD_ARROW_DOWN}
                                        </i>
                                    </Accordion.Toggle>

                                    {!courseActive && (
                                        <>
                                            <div className="btn-aux middle" />
                                            <div className="btn-aux small" />
                                        </>
                                    )}
                                    <Accordion.Collapse className="lesson-container" eventKey={courseIndex.toString()}>
                                        <>
                                            {courseEnrollment.lessonEnrollments &&
                                                courseEnrollment.lessonEnrollments.map(
                                                    (lessonEnrollment: LessonEnrollment, lessonEnrollmentIndex: number) => {
                                                        const lessonStatus = lmsCourseService.getLessonStatus(
                                                            courseEnrollment,
                                                            lessonEnrollment.lesson,
                                                            strings,
                                                        );

                                                        return (
                                                            <Link
                                                                className={`link-to-content ${
                                                                    !canShowContentLink
                                                                        ? 'hide'
                                                                        : lessonStatus !== Constants.courseEnrollmentStatus.BLOCKED &&
                                                                          lessonEnrollment.prerequisitesMet
                                                                        ? ''
                                                                        : 'disable-link'
                                                                }`}
                                                                key={lessonEnrollmentIndex}
                                                                to={linkRouteLesson(courseStudentView, lessonEnrollment)}
                                                            >
                                                                <LMSContentCard
                                                                    key={lessonEnrollment.id}
                                                                    lessonEnrollment={lessonEnrollment}
                                                                    lessonStatus={lmsCourseService.getLessonStatus(
                                                                        courseEnrollment,
                                                                        lessonEnrollment.lesson,
                                                                        strings,
                                                                    )}
                                                                    setCanShowContentLink={setCanShowContentLink}
                                                                />
                                                            </Link>
                                                        );
                                                    },
                                                )}
                                        </>
                                    </Accordion.Collapse>
                                </Accordion>
                            )}
                        </React.Fragment>
                    );
                })}
        </div>
    );
};

export default ProgramCourseLesson;
